<template>
  <div class="ReceiptRowCard">
    <div class="ReceiptRowCard-header">
      <span class="ReceiptRowCard-description">{{ description }}</span>
      <Tag
        class="ReceiptRowCard-tag"
        variant="filled"
        :type="tagStatus"
        size="xs"
      >
        {{ statusLabel }}
      </Tag>
    </div>

    <hr class="ReceiptRowCard-divider">

    <div class="ReceiptRowCard-details">
      <div class="ReceiptRowCard-field">
        <span class="ReceiptRowCard-fieldLabel">{{ t('ui.receipts.table.columns.date') }}</span>
        <span class="ReceiptRowCard-fieldValue">{{ date }}</span>
      </div>

      <div class="ReceiptRowCard-field">
        <span class="ReceiptRowCard-fieldLabel">{{ t('ui.receipts.table.columns.amount') }}</span>
        <span class="ReceiptRowCard-fieldValue">{{ amount }}</span>
      </div>
    </div>

    <Collapsible
      compact
      class="ReceiptRowCard-moreDetails"
    >
      <template #header>
        {{ t('ui.receipts.card.moreDetails') }}
      </template>

      <template #content>
        <div class="ReceiptRowCard-field">
          <span class="ReceiptRowCard-fieldLabel">{{ t('ui.receipts.table.columns.receiptNumber') }}</span>
          <span class="ReceiptRowCard-fieldValue">{{ receiptNumber }}</span>
        </div>

        <div class="ReceiptRowCard-field ReceiptRowCard-field--full">
          <span class="ReceiptRowCard-fieldLabel">{{ t('ui.receipts.table.columns.servicePeriod') }}</span>
          <span class="ReceiptRowCard-fieldValue">{{ servicePeriod }}</span>
        </div>
      </template>
    </Collapsible>

    <div class="ReceiptRowCard-actions">
      <ActionButton
        class="ReceiptRowCard-actionButton"
        variant="secondary"
        size="sm"
        leftIcon="tray-arrow-down"
        :loading="downloadLoading"
        @click="emit('download')"
      >
        {{ t('ui.receipts.card.download') }}
      </ActionButton>

      <ActionButton
        class="ReceiptRowCard-actionButton"
        variant="secondary"
        size="sm"
        leftIcon="pencil"
        @click="emit('edit')"
      >
        {{ t('ui.receipts.card.edit') }}
      </ActionButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import Tag from '@cvo/ui/components/atoms/Tag/Tag.vue';
import Collapsible from '@cvo/ui/components/molecules/Collapsible/Collapsible.vue';
import { useInvoiceStatus } from '@cvo/ui/composables/useInvoiceStatus';
import type { ReceiptRowCardProps } from './ReceiptRowCard.model';

const props = defineProps<ReceiptRowCardProps>();

const emit = defineEmits<{
  download: [];
  edit: [];
}>();

const { t } = useI18n();

const { tagStatus, statusLabel } = useInvoiceStatus(() => props.status);
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .ReceiptRowCard {
    background-color: vars.$organisms-table-row-default-bg-color;
    border-bottom: vars.$organisms-table-row-border-stroke solid vars.$organisms-table-row-border-color;
    border-radius: vars.$organisms-table-row-border-radius;
    padding: vars.$dimensions-d-4 0;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-3;

    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: vars.$dimensions-d-2;
    }

    &-description {
      @include text.text('sm');
      color: vars.$organisms-table-row-title-color;
    }

    &-receiptLabel {
      @include text.text('xs');
      color: vars.$organisms-table-row-text-color;
      flex: 1;
    }

    &-receiptNumber {
      @include text.text('xs');
      color: vars.$organisms-table-row-title-color;
    }

    &-chevron {
      color: vars.$molecules-accordion-icon-color;
    }

    &-divider {
      border: none;
      border-block-start: vars.$organisms-table-row-border-stroke solid vars.$organisms-table-row-border-color;
      margin: 0;
    }

    &-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: vars.$dimensions-d-3;
      margin-bottom: vars.$dimensions-d-5;
    }

    &-field {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-0-5;

      &--full {
        grid-column: 1 / -1;
      }
    }

    &-fieldLabel {
      @include text.text('xs');
      color: vars.$organisms-table-row-text-color;
    }

    &-fieldValue {
      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$organisms-table-row-title-color;
    }

    &-moreDetails {
      .Collapsible-header {
        @include text.text('xs');
        color: vars.$organisms-table-row-text-color;
      }

      .ReceiptRowCard-field:not(:last-child) {
        margin-bottom: vars.$dimensions-d-3;
      }
    }

    &-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: vars.$dimensions-d-3;
    }

    &-actionButton {
      inline-size: 100%;
      justify-content: center;
    }
  }
}
</style>
